import { generatePath } from 'react-router-dom';
import { createApi } from '@reduxjs/toolkit/query/react';

import { URL } from 'api/constants';
import { addParamsToUrl } from 'utils/query';
import { calculateOffset } from 'utils/helpers';

import { apiQuery } from '../../query';

import {
  FiservRetrieval,
  FiservCaseDetail,
  FiservReturnReport,
  FiservRetrievalDetail,
  GetFiservTokenResponse,
  FiservRetrievalsRequest,
  FiservBatchDetailRequest,
  CreateFiservTokenRequest,
  FiservRetrievalsResponse,
  UpdateFiservTokenRequest,
  FiservBatchDetailResponse,
  FiservBatchSummaryRequest,
  CreateFiservTokenResponse,
  FiservFundingGroupsRequest,
  FiservBatchSummaryResponse,
  FiservDepositDetailRequest,
  FiservFundingGroupsResponse,
  FiservDepositDetailResponse,
  FiservDepositSummaryRequest,
  FiservDepositSummaryResponse,
  UpdateFiservRetrievalRequest,
  FiservRetrievalSummaryResponse,
  FiservDepositReportingReadinessResponse,
} from './types';

const tagTypes = ['Fiserv'];

export const fiservApi = createApi({
  tagTypes,
  baseQuery: apiQuery,
  reducerPath: 'fiservApi',
  endpoints: (build) => ({
    getToken: build.query<GetFiservTokenResponse, void>({
      providesTags: tagTypes,
      query: () => ({
        method: 'get',
        url: URL.GET_FISERV_TOKEN,
      }),
    }),

    returnsReport: build.query<FiservReturnReport[], void>({
      providesTags: tagTypes,
      query: () => ({
        method: 'get',
        url: URL.GET_FISERV_RETURNS_REPORT,
      }),
    }),

    deleteToken: build.mutation<CreateFiservTokenResponse, void>({
      invalidatesTags: tagTypes,
      query: () => ({
        method: 'delete',
        url: URL.GET_FISERV_TOKEN,
      }),
    }),

    retrievalsDetail: build.query<FiservRetrievalDetail[], void>({
      providesTags: tagTypes,
      query: () => ({
        method: 'get',
        url: URL.GET_FISERV_RETRIEVALS_DETAIL,
      }),
    }),

    retrievalsSummary: build.query<FiservRetrievalSummaryResponse, void>({
      providesTags: tagTypes,
      query: () => ({
        method: 'get',
        url: URL.GET_FISERV_RETRIEVALS_SUMMARY,
      }),
    }),

    retrievalDetails: build.query<FiservRetrieval, string>({
      providesTags: tagTypes,
      query: (id) => ({
        method: 'get',
        url: generatePath(URL.GET_FISERV_RETRIEVAL, { id }),
      }),
    }),

    caseDetails: build.query<FiservCaseDetail[], string>({
      providesTags: tagTypes,
      query: (id) => ({
        method: 'get',
        url: generatePath(URL.GET_FISERV_CASE_DETAILS, { id }),
      }),
    }),

    addToken: build.mutation<
      CreateFiservTokenResponse,
      CreateFiservTokenRequest
    >({
      invalidatesTags: tagTypes,
      query: (data) => ({
        data,
        method: 'post',
        url: URL.GET_FISERV_TOKEN,
      }),
    }),

    updateToken: build.mutation<
      CreateFiservTokenResponse,
      UpdateFiservTokenRequest
    >({
      invalidatesTags: tagTypes,
      query: (data) => ({
        data,
        method: 'patch',
        url: URL.GET_FISERV_TOKEN,
      }),
    }),

    depositReportingReadiness: build.query<
      FiservDepositReportingReadinessResponse,
      void
    >({
      providesTags: tagTypes,
      query: () => ({
        method: 'get',
        url: URL.GET_FISERV_DEPOSIT_REPORTING_READINESS,
      }),
    }),

    batchDetail: build.query<
      FiservBatchDetailResponse,
      FiservBatchDetailRequest
    >({
      providesTags: tagTypes,
      query: (params) => ({
        method: 'get',
        url: addParamsToUrl(URL.GET_FISERV_BATCH_DETAIL, params),
      }),
    }),

    paymentDetail: build.query<
      FiservDepositDetailResponse,
      FiservDepositDetailRequest
    >({
      providesTags: tagTypes,
      query: (params) => ({
        method: 'get',
        url: addParamsToUrl(URL.GET_FISERV_PAYMENT_DETAIL, params),
      }),
    }),

    fundingGroups: build.query<
      FiservFundingGroupsResponse,
      FiservFundingGroupsRequest
    >({
      providesTags: tagTypes,
      query: (params) => ({
        method: 'get',
        url: addParamsToUrl(URL.GET_FISERV_FUNDING_GROUPS, params),
      }),
    }),

    batchSummaryByMerchant: build.query<
      FiservBatchSummaryResponse,
      FiservBatchSummaryRequest
    >({
      providesTags: tagTypes,
      query: (params) => ({
        method: 'get',
        url: addParamsToUrl(URL.GET_FISERV_BATCH_SUMMARY_BY_MERCHANT, params),
      }),
    }),

    paymentSummaryByMerchant: build.query<
      FiservDepositSummaryResponse,
      FiservDepositSummaryRequest
    >({
      providesTags: tagTypes,
      query: (params) => ({
        method: 'get',
        url: addParamsToUrl(URL.GET_FISERV_PAYMENT_SUMMARY_BY_MERCHANT, params),
      }),
    }),

    updateRetrieval: build.mutation<
      FiservRetrieval,
      UpdateFiservRetrievalRequest
    >({
      invalidatesTags: tagTypes,
      query: ({ id, ...data }) => ({
        data,
        method: 'patch',
        url: generatePath(URL.UPDATE_FISERV_RETRIEVAL, { id: String(id) }),
      }),
    }),

    depositReportingPaymentSummary: build.query<
      FiservDepositSummaryResponse,
      FiservDepositSummaryRequest
    >({
      providesTags: tagTypes,
      query: (params) => ({
        method: 'get',
        url: addParamsToUrl(
          URL.GET_FISERV_DEPOSIT_REPORTING_PAYMENT_SUMMARY,
          params
        ),
      }),
    }),

    retrievals: build.query<FiservRetrievalsResponse, FiservRetrievalsRequest>({
      providesTags: tagTypes,
      query: (params) => {
        const { page = 1, limit = 20, ...otherParams } = params || {};
        const offset = calculateOffset(page, limit);
        const searchParams = {
          limit,
          offset,
          ...otherParams,
        };
        return {
          method: 'get',
          url: addParamsToUrl(URL.GET_FISERV_RETRIEVALS, searchParams),
        };
      },
    }),
  }),
});

export const {
  useGetTokenQuery,
  useRetrievalsQuery,
  useAddTokenMutation,
  useCaseDetailsQuery,
  useBatchDetailQuery,
  useReturnsReportQuery,
  usePaymentDetailQuery,
  useFundingGroupsQuery,
  useDeleteTokenMutation,
  useUpdateTokenMutation,
  useLazyBatchDetailQuery,
  useRetrievalsDetailQuery,
  useRetrievalDetailsQuery,
  useRetrievalsSummaryQuery,
  useUpdateRetrievalMutation,
  useLazyRetrievalDetailsQuery,
  useBatchSummaryByMerchantQuery,
  usePaymentSummaryByMerchantQuery,
  useDepositReportingReadinessQuery,
  useDepositReportingPaymentSummaryQuery,
} = fiservApi;
